import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { generateMediaRoomId } from '../utils/chat/utils';
import { messageService } from '../utils/chat/message';

const MEDIA_SERVER_URI =
  process.env.REACT_APP_MEDIA_SERVER_URL || "https://ai.dedahospital.com";

const MediaSocketContext = createContext();

export const MediaSocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [appToken, setAppToken] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  // Connect to socket
  const connectMediaSocket = (doctorId, token) => {
    if (doctorId && token && !socket) {
      const newSocket = io(MEDIA_SERVER_URI, {
        transports: ['websocket'],
        autoConnect: true,
        query: {
          userId: `${doctorId}`,
          token: token || localStorage.getItem('token'),
        },
      });

      setAppToken(token);
      setSocket(newSocket);

      newSocket.on('connect', () => {
        console.log('Doctor socket connected:', newSocket?.id, doctorId, token);
        setIsConnected(true);
      });

      newSocket.on('disconnect', () => {
        console.log('Doctor socket disconnected');
        setIsConnected(false);
      });

      return newSocket;
    }
    return socket;
  };

  // Disconnect socket
  const disconnectMediaSocket = () => {
    if (socket) {
      socket.disconnect();
      setSocket(null);
      setIsConnected(false);
    }
  };

  // Register user
  const register = (userId) => {
    if (!socket) return;

    socket.emit('register', { userId }, (res) => {
      if (res) {
        console.log('[Register] Socket registered successfully:', res);
      } else {
        console.log('[Register] Socket registration failed:', res);
      }
    });
  };

  // Subscribe to media events
  const subscribeMediaIO = (onCallRequest) => {
    if (!socket) {
      console.log('[subscribeMediaIO] Socket not initialized');
      return;
    }

    socket.on('private-message', (data) => {
      console.log(`'private-message' to socket.io`, data);
      messageService.sendMessage({ type: 'private-media-message', data });
    });

    socket.on('call-request', (data) => {
      console.log('📞 Incoming call request:', data);
      if (onCallRequest) {
        onCallRequest(data);
      }
    });

    socket.on('accept-call', (data) => {
      console.log('Call accepted:', data);
    });

    socket.on('call-rejected', (data) => {
      console.log(' Call rejected:', data);
    });

    socket.on('call-initiated', (data) => {
      console.log('📞 Call initiated:', data);
    });

    socket.on('peer-disconnected', (data) => {
      console.log('Peer disconnected:', data);
    });
  };

  // Get recent media chats
  const getRecentMediaChats = ({ doctorId, token }, cb) => {
    if (!socket) return;

    socket.emit('get-recent-patient-chats', { userId: doctorId, token }, (res) => {
      if (cb) {
        const chats = Array.isArray(res) ? res : [];
        cb(chats);
      }
    });
  };

  // Get chat history by patient
  const getChatHistoryByPatient = ({ doctorId, patientId }, cb) => {
    if (!socket) {
      console.log('Socket not initialized');
      return;
    }

    socket.emit(
      'join-room',
      {
        roomId: generateMediaRoomId({ patientId, doctorId }),
        userId: patientId,
        userType: 'patient',
        roomType: 'chat',
        token: localStorage.getItem('token'),
      },
      (res) => {
        if (!res || !res.rtpCapabilities) {
          console.log('Call back failed, Joining room');
        }
      }
    );

    socket.emit('get-chat-history', { currentUserId: `${patientId}`, withUserId: `${doctorId}` }, (res) => {
      console.log('chats from socket');
      if (cb) {
        const chats = Array.isArray(res) ? res : [];
        cb(chats);
      }
    });
  };

  // Send media message
  const sendMediaMessage = (data) => {
    if (!socket) return;
    socket.emit('private-message', data);
  };

  // Join media chat room
  const joinMediaChatRoom = ({ roomId, doctorId, roomType }) => {
    if (!socket) return;

    socket.emit(
      'join-room',
      {
        roomId: `${roomId}`,
        userId: `${doctorId}`,
        userType: 'patient',
        roomType,
        token: appToken,
      },
      (res) => {
        if (!res || !res.rtpCapabilities) {
          console.log('Call back failed, Joining room');
        }
      }
    );
  };

  // Leave media chat room
  const leaveMediaChatRoom = ({ roomId, doctorId, roomType = 'chat' }) => {
    if (!socket) return;

    socket.emit('leave-room', { roomId: `${roomId}`, userId: `${doctorId}`, roomType }, (res) => {
      if (res && res.success) {
        console.log('Left media chat room successfully');
      } else {
        console.log('Failed to leave media chat room');
      }
    });
  };

  // Mark message as read
  const markMessageAsRead = ({ fromUserId, toUserId }) => {
    if (!socket) return;
    socket.emit('mark-as-read', { fromUserId, toUserId });
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [socket]);

  return (
    <MediaSocketContext.Provider
      value={{
        socket,
        appToken,
        isConnected,
        connectMediaSocket,
        disconnectMediaSocket,
        register,
        subscribeMediaIO,
        getRecentMediaChats,
        getChatHistoryByPatient,
        sendMediaMessage,
        joinMediaChatRoom,
        leaveMediaChatRoom,
        markMessageAsRead,
      }}
    >
      {children}
    </MediaSocketContext.Provider>
  );
};

export const useMediaSocket = () => {
  const context = useContext(MediaSocketContext);
  if (!context) {
    throw new Error('useMediaSocket must be used within a MediaSocketProvider');
  }
  return context;
};